import { Text, Stack, Badge } from '@repletoryreach/sdk-ui';
import { useT } from '../../../i18n/I18nProvider';
import type { Doc, SchemaObject } from '../spec';
import { readFields, describeType, refName } from '../spec';

/**
 * Renders a schema as a table of fields.
 *
 * Every row comes from the document, so a field the service adds appears here
 * without anybody editing this page, and a field it removes disappears.
 */
export function SchemaTable({
  schema,
  doc,
  emptyLabel
}: {
  schema: SchemaObject | undefined;
  doc: Doc | undefined;
  emptyLabel?: string;
}) {
  const t = useT();
  const fields = readFields(schema, doc);

  if (fields.length === 0) {
    const name = refName(schema);
    const type = schema ? describeType(schema, doc) : null;
    return (
      <Text role="caption" c="var(--rr-color-text-muted)">
        {name ?? type ?? emptyLabel ?? t('developers.schema.none')}
      </Text>
    );
  }

  return (
    <div className="rr-dev__table-wrap">
      <table className="rr-dev__table">
        <thead>
          <tr>
            <th scope="col">{t('developers.schema.field')}</th>
            <th scope="col">{t('developers.schema.type')}</th>
            <th scope="col">{t('developers.schema.notes')}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <tr key={`${field.depth}-${field.name}-${index}`}>
              <td>
                <span
                  className="rr-dev__field"
                  style={{ paddingInlineStart: `calc(var(--rr-spacing-md) * ${field.depth})` }}
                >
                  <code>{field.name}</code>
                  {field.required && (
                    <Badge size="xs" color="warning" variant="light">
                      {t('developers.schema.required')}
                    </Badge>
                  )}
                </span>
              </td>
              <td>
                <code className="rr-dev__type">{field.type}</code>
              </td>
              <td>
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {field.description ?? ''}
                </Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The same table shape for parameters, which carry `in` and `required` themselves. */
export function ParameterTable({
  parameters,
  doc
}: {
  parameters: { name: string; in: string; required?: boolean; description?: string; schema?: SchemaObject }[];
  doc: Doc | undefined;
}) {
  const t = useT();
  if (parameters.length === 0) return null;

  return (
    <Stack gap="xs">
      <div className="rr-dev__table-wrap">
        <table className="rr-dev__table">
          <thead>
            <tr>
              <th scope="col">{t('developers.schema.parameter')}</th>
              <th scope="col">{t('developers.schema.in')}</th>
              <th scope="col">{t('developers.schema.type')}</th>
              <th scope="col">{t('developers.schema.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((p) => (
              <tr key={`${p.in}-${p.name}`}>
                <td>
                  <span className="rr-dev__field">
                    <code>{p.name}</code>
                    {p.required && (
                      <Badge size="xs" color="warning" variant="light">
                        {t('developers.schema.required')}
                      </Badge>
                    )}
                  </span>
                </td>
                <td>
                  <Text role="caption">{p.in}</Text>
                </td>
                <td>
                  <code className="rr-dev__type">{describeType(p.schema, doc)}</code>
                </td>
                <td>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {p.description ?? ''}
                  </Text>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Stack>
  );
}
